import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { inputCls } from "./utils";

// CSV import — accepts a paste-or-upload textarea keyed to the
// migrations importer endpoint. The result banner surfaces the first
// 5 row-level errors so the operator can fix the CSV without hunting
// through server logs.

export function ImportModal({ partnerCode, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  }

  async function submit() {
    if (!text.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/commissions/migrations/${partnerCode}/import`, { csv_text: text });
      setResult(data);
      if (data.created > 0) toast.success(`Imported ${data.created} clients`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display text-xl tracking-tight">Import CSV</h3>
            <p className="text-sm text-slate-400 mt-1">
              Column headers can be: business_name, contact_name, email, phone, service_address, abn, current_service, chosen_plan, notes. Common synonyms (Customer, Company, Address, etc.) are auto-mapped.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file" className="text-sm text-slate-400 mb-3" />
        <textarea
          data-testid="import-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          className={inputCls + " font-mono text-xs"}
          placeholder="Paste CSV content here (or upload above)…"
        />
        {result && (
          <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm" data-testid="import-result">
            <div>
              <b className="text-emerald-300">Created:</b> {result.created} ·{" "}
              <b className="text-slate-300">Skipped (duplicates/empty):</b> {result.skipped} of {result.total_rows}
            </div>
            {result.errors?.length > 0 && (
              <ul className="mt-2 text-xs text-amber-300 list-disc pl-5 space-y-0.5">
                {result.errors.slice(0, 5).map((er, i) => <li key={`${i}-${String(er).slice(0, 40)}`}>{er}</li>)}
                {result.errors.length > 5 && <li>… and {result.errors.length - 5} more</li>}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={result ? onDone : onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">
            {result ? "Close & refresh" : "Cancel"}
          </button>
          {!result && (
            <button
              data-testid="import-submit"
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Importing…" : "Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
